import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mediaUrl } from '@/lib/api';
import { Volume2, VolumeX, ArrowRight } from 'lucide-react';

/**
 * Prominent vertical "reel" of Kaia, the face of the AI coach. Autoplays her
 * intro reel muted on loop (lively), links to her profile, and offers a sound
 * toggle. muted is set via ref so browsers don't block autoplay.
 */
export const KaiaReel = ({ className = '' }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  // Re-assert muted on mount and whenever the localized reel swaps in.
  useEffect(() => { if (ref.current) ref.current.muted = true; setMuted(true); }, [i18n.resolvedLanguage, i18n.language]);

  // Localized hero reel: Dutch -> Flemish, German -> German, else English.
  const lang = (i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2).toLowerCase();
  const reelSrc = mediaUrl(`/api/coach-video-file/intro?lang=${lang}`);

  const toggleSound = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
  };

  return (
    <div className={`relative ${className}`} data-testid="kaia-reel">
      <span className="absolute -inset-4 rounded-[2.4rem] bg-[#00B4D8]/20 blur-3xl opacity-70 pointer-events-none" aria-hidden="true" />
      <button
        type="button"
        onClick={() => navigate('/kaia')}
        aria-label={t('landing.kaiaReelCta')}
        className="group relative block w-full overflow-hidden rounded-[1.75rem] border border-[#00B4D8]/30 shadow-2xl aspect-[9/16] bg-black text-left"
      >
        <video
          ref={ref}
          key={lang}
          src={reelSrc}
          poster={mediaUrl('/api/static/coach/kaia_portrait.png')}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="w-full h-full object-cover"
        />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur border border-white/15 px-2.5 py-1 text-[11px] font-medium text-white/90">
          <span className="w-1.5 h-1.5 rounded-full bg-[#2EE59D] animate-pulse" /> {t('coach.liveBadge')}
        </span>
        <span className="absolute inset-x-0 bottom-0 p-4 pt-12 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
          <span className="block font-display text-xl text-white">{t('landing.kaiaReelName')}</span>
          <span className="inline-flex items-center gap-1 text-xs text-[#48CAE4] font-semibold mt-0.5 group-hover:gap-2 transition-all">
            {t('landing.kaiaReelCta')} <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={toggleSound}
        data-testid="kaia-reel-sound"
        aria-label={muted ? t('coach.unmute') : t('coach.mute')}
        className="absolute bottom-3.5 right-3.5 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur border border-white/20 px-3 py-1.5 text-xs font-medium text-white hover:bg-black/80 transition-colors"
      >
        {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5 text-[#48CAE4]" />}
        {muted ? t('coach.unmute') : t('coach.mute')}
      </button>
    </div>
  );
};
